import React, { useState, useEffect, useContext } from 'react';
import { Container, Table, Button, Row, Col, Badge, Alert, InputGroup, Form } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import api from '../../services/api';
import './Customer.css';

const CustomerList = () => {
  const { user } = useContext(AuthContext);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  
  // 確保只有管理員可以訪問
  useEffect(() => {
    if (user?.role !== 0) {
      navigate('/');
    }
  }, [user, navigate]);
  
  // 載入客戶列表
  useEffect(() => {
    if (user?.role === 0) {
      loadCustomers();
    }
  }, [user]);
  
  const loadCustomers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/customers');
      setCustomers(response.data.data || []);
      setError('');
    } catch (err) {
      console.error('載入客戶失敗:', err);
      setError('載入客戶失敗: ' + (err.response?.data?.message || '請稍後再試'));
    } finally {
      setLoading(false);
    }
  };
  
  // 篩選客戶
  const filteredCustomers = searchTerm
    ? customers.filter(customer => 
        customer.cust_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.cust_name.toLowerCase().includes(searchTerm.toLowerCase()))
    : customers;

  return (
    <Container className="customer-list-container">
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6}>
          <h2>客戶管理</h2>
        </Col>
        <Col xs={12} md={6} className="text-md-end">
          <Button variant="outline-secondary" className="me-2" onClick={loadCustomers}>
            刷新
          </Button>
          <Button variant="primary" as={Link} to="/customers/new">
            新增客戶
          </Button>
        </Col>
      </Row>
      
      <Row className="mb-3">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              placeholder="搜尋客戶編號或名稱"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button variant="outline-secondary" onClick={() => setSearchTerm('')}>
                清除
              </Button>
            )}
          </InputGroup>
        </Col>
      </Row>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      {loading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
        </div>
      ) : filteredCustomers.length === 0 ? (
        <div className="text-center my-5">
          {searchTerm ? (
            <p>沒有符合搜尋條件的客戶</p>
          ) : (
            <>
              <p>尚無客戶記錄</p>
              <Button variant="primary" as={Link} to="/customers/new">
                新增客戶
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="table-responsive">
          <Table hover className="customer-table">
            <thead>
              <tr>
                <th>客戶編號</th>
                <th>客戶名稱</th>
                <th>上級客戶</th>
                <th>地址</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {filteredCustomers.map((customer) => (
                <tr key={customer.cust_code}>
                  <td>{customer.cust_code}</td>
                  <td>{customer.cust_name}</td>
                  <td>{customer.parent || '-'}</td>
                  <td>
                    {customer.addr1}
                    {customer.addr2 && <br />}
                    {customer.addr2}
                    {customer.addr3 && <br />}
                    {customer.addr3}
                  </td>
                  <td>
                    <Button 
                      variant="link" 
                      className="p-0 text-primary me-2" 
                      as={Link} 
                      to={`/customers/edit/${customer.cust_code}`}
                    >
                      編輯
                    </Button>
                    
                    <Button 
                      variant="link" 
                      className="p-0 text-secondary me-2" 
                      as={Link} 
                      to={`/customers/${customer.cust_code}/schedule`}
                    >
                      配送時間
                    </Button>
                    
                    <Button 
                      variant="link" 
                      className="p-0 text-info" 
                      as={Link} 
                      to={`/customers/${customer.cust_code}/relations`}
                    >
                      用戶關聯
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </Container>
  );
};

export default CustomerList;